import { fetchWorkflowById } from "@/app/(v2)/workflows/_hooks/workflowsApi";
import { workflowsKeys } from "@/app/(v2)/workflows/_hooks/workflowsQueryKeys";
import {
  ToastDetailCard,
  type ToastButtonItem,
  type ToastLinkItem,
  type ToastMetaItem,
} from "@/components/toasts/RichToastContent";
import { getQueryClient } from "@/lib/queryClient";
import { ROUTES, buildLoginRedirectUrl } from "@/lib/routes";
import { toast } from "@/lib/toast";
import { getContextualWorkflowRunUrl } from "@/lib/utils/urlHelpers";
import type { Workflow } from "@/stores/workflowsStore";
import type { DeployResult } from "@/types/contract";
import type { TerminalRunStatus } from "@/types/runStream";
import type { ReactNode } from "react";

type RichToastType = "success" | "error" | "info" | "warning";

interface RichToastButton {
  title: string;
  href: string;
  external?: boolean;
  variant?: "brand" | "black";
}

interface RichToastInput {
  id?: string;
  type: RichToastType;
  title: string;
  summary?: ReactNode;
  description?: ReactNode;
  meta?: ToastMetaItem[];
  links?: ToastLinkItem[];
  button?: RichToastButton;
  duration?: number | null;
}

function createToastId(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

function resolveToastMethod(type: RichToastType) {
  if (type === "success") return toast.success;
  if (type === "error") return toast.error;
  if (type === "warning") return toast.warning;
  return toast.info;
}

export function compactId(value: string, start: number = 6, end: number = 4): string {
  if (value.length <= start + end + 1) return value;
  return `${value.slice(0, start)}...${value.slice(-end)}`;
}

export function getWorkflowName(workflowId?: string | null, fallback: string = "Workflow"): string {
  if (!workflowId) return fallback;
  // Toast helpers are called from non-React contexts, so we read straight
  // from the shared QueryClient cache instead of taking a React subscription.
  const qc = getQueryClient();
  // Prefer an already-resolved detail entry (full data, written by useWorkflow).
  const cached = qc.getQueryData<Workflow>(workflowsKeys.detail(workflowId));
  if (cached?.name) return cached.name;
  // Fall back to any list page that happens to contain this workflow.
  // Run-streamed toasts often fire before the user ever opens the workflow
  // detail, so the list cache is usually the only populated source.
  type ListPage = { items?: Workflow[] };
  const listQueries = qc.getQueriesData<ListPage>({ queryKey: ["workflows", "list"] });
  for (const [, data] of listQueries) {
    const match = data?.items?.find(item => item.id === workflowId);
    if (match?.name) return match.name;
  }
  // Cache miss: kick off an async detail fetch (fire-and-forget) so the
  // NEXT toast for this workflow finds a name. The current workflow list
  // cache only has the visible page, so org-activity toasts for workflows
  // on other pages otherwise render the fallback literal.
  // We don't await — toast helpers are sync and a single miss is acceptable.
  void qc
    .fetchQuery({
      queryKey: workflowsKeys.detail(workflowId),
      queryFn: () => fetchWorkflowById(workflowId),
      staleTime: 30_000,
    })
    .catch(() => {
      // 404/unauthorized is expected for workflows the caller can't see;
      // swallow so toast rendering never throws.
    });
  return fallback;
}

export function getWorkflowEditorHref(workflowId: string): string {
  return `${ROUTES.WORKFLOW_EDIT}?id=${workflowId}`;
}

export function getRunHistoryHref(runId: string): string {
  return `${ROUTES.WORKFLOW_HISTORY}/${runId}`;
}

export function getContextualRunHistoryHref(runId: string, workflowId?: string | null): string {
  return getContextualWorkflowRunUrl(runId, workflowId);
}

export function getContractHref(contractId: string): string {
  return `${ROUTES.CONTRACTS}/${contractId}`;
}

export function getContractDeploymentsHref(contractId: string): string {
  return `${ROUTES.CONTRACTS}/${contractId}/deployments`;
}

export function getLoginRedirectHref(currentPath?: string): string {
  if (currentPath) {
    return buildLoginRedirectUrl(currentPath);
  }

  if (typeof window !== "undefined") {
    return buildLoginRedirectUrl(window.location.pathname + window.location.search);
  }

  return ROUTES.LOGIN;
}

export function showRichToast({
  id,
  type,
  title,
  summary,
  description,
  meta = [],
  links = [],
  button,
  duration = 7000,
}: RichToastInput): string {
  const toastId = id ?? createToastId("toast");
  const method = resolveToastMethod(type);

  const cardButton: ToastButtonItem | undefined = button
    ? {
        label: button.title,
        href: button.href,
        external: button.external,
        variant: button.variant,
      }
    : undefined;

  const hasContent = summary || description || meta.length > 0 || links.length > 0 || cardButton;

  return method({
    id: toastId,
    title,
    description: hasContent ? (
      <ToastDetailCard
        toastId={toastId}
        summary={summary}
        description={description}
        meta={meta}
        links={links}
        button={cardButton}
      />
    ) : undefined,
    duration,
  });
}

interface WorkflowRunToastInput {
  status: "started" | "rerun_started" | "retry_started" | TerminalRunStatus;
  runId: string;
  workflowId?: string | null;
  workflowName?: string | null;
  durationLabel?: string;
  cuLabel?: string;
  sourceLabel?: string;
  errorMessage?: string;
}

export function showWorkflowRunToast({
  status,
  runId,
  workflowId,
  workflowName,
  durationLabel,
  cuLabel,
  sourceLabel,
  errorMessage,
}: WorkflowRunToastInput): string {
  const resolvedWorkflowName = workflowName || getWorkflowName(workflowId);
  const meta: ToastMetaItem[] = [];

  if (sourceLabel) {
    meta.push({ label: sourceLabel });
  }
  if (durationLabel) {
    meta.push({ label: durationLabel });
  }
  if (cuLabel) {
    meta.push({ label: cuLabel });
  }

  const links: ToastLinkItem[] = [];
  if (workflowId) {
    links.push({ label: "Open workflow", href: getWorkflowEditorHref(workflowId) });
  }

  if (status === "started" || status === "rerun_started" || status === "retry_started") {
    return showRichToast({
      type: "info",
      title:
        status === "retry_started" ? "Retry started" : status === "rerun_started" ? "Rerun started" : "Run started",
      summary: resolvedWorkflowName,
      meta,
      links,
      button: { title: "View run", href: getContextualRunHistoryHref(runId, workflowId) },
      duration: 6500,
    });
  }

  if (status === "success") {
    return showRichToast({
      type: "success",
      title: "Run completed",
      summary: resolvedWorkflowName,
      meta,
      links,
      button: { title: "View run", href: getContextualRunHistoryHref(runId, workflowId) },
      duration: 8000,
    });
  }

  if (status === "cancelled") {
    return showRichToast({
      type: "info",
      title: "Run cancelled",
      summary: resolvedWorkflowName,
      meta,
      links,
      button: { title: "View run", href: getContextualRunHistoryHref(runId, workflowId) },
      duration: 7000,
    });
  }

  if (status === "partial") {
    return showRichToast({
      type: "warning",
      title: "Run partially completed",
      summary: resolvedWorkflowName,
      description: errorMessage,
      meta,
      links,
      button: { title: "View run", href: getContextualRunHistoryHref(runId, workflowId) },
      duration: 9000,
    });
  }

  return showRichToast({
    type: "error",
    title: "Run failed",
    summary: resolvedWorkflowName,
    description: errorMessage,
    meta,
    links,
    button: { title: "Inspect run", href: getContextualRunHistoryHref(runId, workflowId) },
    duration: 9000,
  });
}

interface WorkflowStateToastInput {
  type: RichToastType;
  title: string;
  workflowId?: string | null;
  workflowName?: string | null;
  description?: ReactNode;
  meta?: ToastMetaItem[];
  links?: ToastLinkItem[];
  button?: RichToastButton;
  duration?: number | null;
  omitOpenWorkflowLink?: boolean;
}

export function showWorkflowStateToast({
  type,
  title,
  workflowId,
  workflowName,
  description,
  meta = [],
  links = [],
  button,
  duration = 7500,
  omitOpenWorkflowLink = false,
}: WorkflowStateToastInput): string {
  const resolvedWorkflowName = workflowName || getWorkflowName(workflowId);
  const nextLinks = [...links];

  if (!omitOpenWorkflowLink && workflowId && !nextLinks.some(link => link.href === getWorkflowEditorHref(workflowId))) {
    nextLinks.unshift({ label: "Open workflow", href: getWorkflowEditorHref(workflowId) });
  }

  return showRichToast({
    type,
    title,
    summary: resolvedWorkflowName,
    description,
    meta,
    links: nextLinks,
    button,
    duration,
  });
}

interface ContractStatusToastInput {
  type: RichToastType;
  title: string;
  contractId: string;
  contractName: string;
  chainName?: string;
  description?: ReactNode;
  deployment?: DeployResult;
  explorerUrl?: string;
  button?: RichToastButton;
  duration?: number | null;
}

export function showContractStatusToast({
  type,
  title,
  contractId,
  contractName,
  chainName,
  description,
  deployment,
  explorerUrl,
  button,
  duration = 8500,
}: ContractStatusToastInput): string {
  const address = deployment?.contractAddress ?? deployment?.address;
  const txHash = deployment?.transactionHash ?? deployment?.txHash;
  const links: ToastLinkItem[] = [{ label: "Open contract", href: getContractHref(contractId) }];
  const meta: ToastMetaItem[] = [];

  if (chainName) {
    meta.push({ label: chainName });
  }
  if (address) {
    meta.push({ label: compactId(address) });
  }
  if (txHash) {
    meta.push({ label: compactId(txHash) });
  }
  if (explorerUrl) {
    links.push({ label: "Explorer", href: explorerUrl, external: true });
  }

  return showRichToast({
    type,
    title,
    summary: contractName,
    description,
    meta,
    links,
    button: button ?? { title: "View deployments", href: getContractDeploymentsHref(contractId) },
    duration,
  });
}

interface OrgContextToastInput {
  type: RichToastType;
  title: string;
  orgName: string;
  description?: ReactNode;
  button?: RichToastButton;
  links?: ToastLinkItem[];
  duration?: number | null;
}

export function showOrgContextToast({
  type,
  title,
  orgName,
  description,
  button,
  links = [],
  duration = 7000,
}: OrgContextToastInput): string {
  return showRichToast({
    type,
    title,
    summary: orgName,
    description,
    links,
    button,
    duration,
  });
}

export function showSessionExpiredToast(currentPath?: string): string {
  const loginHref = getLoginRedirectHref(currentPath);

  return showRichToast({
    id: "auth-required",
    type: "error",
    title: "Session expired",
    summary: "Your workspace needs a fresh login.",
    description: "Sign in again to continue running workflows, deploying contracts, or editing organization data.",
    meta: [{ label: "auth required" }],
    button: { title: "Log in again", href: loginHref },
    duration: 10000,
  });
}
